import React, { useState, useEffect, useRef } from 'react';
import './navbar.css';
import logo from '../../Assets/logo.webp';
import { Link } from 'react-scroll';

const NAV_LINKS = [
  { label: 'About Us',     to: 'About'     },
  { label: 'Gallery',      to: 'Gallery' },
  { label: 'Services',     to: 'projects'  },
  { label: 'Achievements', to: 'paper'     },
  { label: 'Testimonials', to: 'reviews'   },
];

const Navbar = () => {
  const [menuOpen,   setMenuOpen]   = useState(false);
  const [activeLink, setActiveLink] = useState('');
  const [galleryInView, setGalleryInView] = useState(false);
  const drawerRef = useRef(null);

  /* Gallery section in view → blur trigger */
  useEffect(() => {
    const galleryElement = document.getElementById('Education');
    if (!galleryElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => setGalleryInView(entry.isIntersecting),
      { threshold: 0.1 }
    );

    observer.observe(galleryElement);
    return () => observer.disconnect();
  }, []);

  /* Close drawer on Escape */
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /* Lock body scroll when drawer is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <nav className={`navbar ${galleryInView ? 'navbar--gallery-blur' : ''}`} role="navigation" aria-label="Main navigation">

        {/* ── LOGO ── */}
        <Link to="intro" smooth duration={600} className="nav-logo-link" aria-label="Go to top">
          <img src={logo} alt="Shree Photos & Films" className="nav-logo" />
          <div className="nav-brand">
            <span className="nav-brand-primary">Shree Photos Films</span>
          </div>
        </Link>

        {/* ── DESKTOP MENU ── */}
        <ul className="desktop-menu">
          {NAV_LINKS.map(({ label, to }) => (
            <li key={to}>
              <Link
                to={to}
                smooth
                duration={600}
                spy
                onSetActive={() => setActiveLink(to)}
                className={`desktop-link ${activeLink === to ? 'desktop-link--active' : ''}`}
              >
                {label}
                <span className="link-underline" />
              </Link>
            </li>
          ))}
        </ul>

        {/* ── CONTACT BUTTON ── */}
        <Link to="Contact" smooth duration={600} className="contact-btn" aria-label="Contact Us">
          <span className="contact-btn__icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.36 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.34 1.85.573 2.81.7A2 2 0 0122 14z"/>
            </svg>
          </span>
          <span>Contact Us</span>
          <span className="contact-btn__shine" />
        </Link>

        {/* ── HAMBURGER ── */}
        <button
          className={`hamburger ${menuOpen ? 'hamburger--open' : ''}`}
          onClick={() => setMenuOpen(v => !v)}
          aria-expanded={menuOpen}
          aria-label="Toggle navigation menu"
        >
          <span className="ham-bar" />
          <span className="ham-bar" />
          <span className="ham-bar" />
        </button>
      </nav>

      {/* ── MOBILE DRAWER ── */}
      <div
        className={`mobile-overlay ${menuOpen ? 'mobile-overlay--open' : ''}`}
        onClick={closeMenu}
        aria-hidden="true"
      />

      <aside
        ref={drawerRef}
        className={`mobile-drawer ${menuOpen ? 'mobile-drawer--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
      >
        {/* Drawer header */}
        <div className="drawer-header">
          <img src={logo} alt="Shree Photos & Films" className="drawer-logo" />
          <div className="drawer-brand">
            <span className="drawer-brand-primary">Shree Photos</span>
            <span className="drawer-brand-secondary">&amp; Films</span>
          </div>
          <button className="drawer-close" onClick={closeMenu} aria-label="Close navigation">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Divider */}
        <div className="drawer-divider">
          <span />
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#c9a84c"><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4z"/></svg>
          <span />
        </div>

        {/* Drawer links */}
        <nav className="drawer-nav" role="navigation">
          {[...NAV_LINKS, { label: 'Contact Us', to: 'Contact' }].map(({ label, to }, i) => (
            <Link
              key={to}
              to={to}
              smooth
              duration={600}
              className="drawer-link"
              onClick={closeMenu}
              style={{ animationDelay: menuOpen ? `${i * 0.06 + 0.15}s` : '0s' }}
            >
              <span className="drawer-link__num">0{i + 1}</span>
              <span className="drawer-link__label">{label}</span>
              <span className="drawer-link__arrow">→</span>
            </Link>
          ))}
        </nav>

        {/* Drawer footer */}
        <div className="drawer-footer">
          <p>Est. 2017 · Surat, Gujarat</p>
          <p>Capturing Moments That Last Forever</p>
        </div>
      </aside>
    </>
  );
};

export default Navbar;
